<template>
  <div style="display: none" />
</template>

<script>
import { mapGetters } from 'vuex'

import { EventBus } from '@/services/eventbus'

export default {
  name: 'WhiteSpotsLayer',
  props: {
    /**
     * Indicates whether the MapBox instance is ready
     */
    loaded: {
      type: Boolean,
      required: true,
    },
    layerVisibility: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      layerName: 'witte_vlekken_plan',
      type: 'fill',
      minzoom: 10,
      paint: {
        'fill-color': 'lightgreen',
        'fill-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          0,
          14,
          0.50,
        ],
      },

      textLayout: {
       'text-field': ['get', 'grid_ID'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-radial-offset': 0.5,
        'symbol-avoid-edges': true,
        'text-justify': 'auto',
        'symbol-placement': 'point',
        'visibility': this.layerVisibility ? 'visible' : 'none',
      },
      textPaint: {
        'text-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          0,
          14,
          0.80,
        ],
      },
      layout: {
        'visibility': this.layerVisibility ? 'visible' : 'none',
      },
      colors: {
        Lichtgrijs: 'grey',
        Wit: 'white',
        Groen: 'green',
        Lichtblauw: 'lightblue',
        Blauw: 'blue',
        Lichtgroen: 'lightgreen',
      },
      selectedFeatureState: null,
    }
  },
  computed: {
    ...mapGetters('deployment', [
      'MapboxAccount',
      'DeploymentLayerName',
    ]),
    source() {
      return {
        url: process.env.VUE_APP_BL_URL || `mapbox://${this.MapboxAccount}.${this.sourceName}`,
        layer: process.env.VUE_APP_BL_SOURCE || this.sourceName,
      }
    },
    sourceName() {
      return this.DeploymentLayerName({ layer: this.layerName })
    },
  },
  watch: {
    loaded: {
      immediate: true,
      handler(loaded) {
        if (!loaded) return

        this.addLayer()
      },
    },
    layerVisibility: {
      handler(visible) {
        !visible ? this.removeLayerAndSource() : this.addLayer()
      },
    },
  },
  beforeDestroy() {
    this.unregisterLayerInteractions()
    this.clearSelectedState()
  },
  methods: {
    /**
     * Add the layer
     */
    addLayer() {
      this.removeLayerAndSource()

      if (! this.sourceName) return

      if (! this.source.url || ! this.source.layer) return

      if (this.$store.map.getLayer(this.layerName)) {
        this.$store.map.removeLayer(this.layerName)
        this.$store.map.removeLayer(this.layerName + '-text')
      }

      if (this.$store.map.getSource(this.sourceName)) {
        this.$store.map.removeSource(this.sourceName)
      }

      this.$nextTick(() => {
        this.$store.map.addSource(this.sourceName, {
          type: 'vector',
          url: this.source.url,
        })

        this.$store.map.addLayer({
          'id': this.layerName,
          'type': this.type,
          'source': this.sourceName,
          'source-layer': this.source.layer,
          'minzoom': this.minzoom || 0,
          'paint': this.paint,
          'layout': this.layout,
        })

        this.$store.map.addLayer({
          'id': this.layerName + '-text',
          'type': 'symbol',
          'source': this.sourceName,
          'source-layer': this.source.layer,
          'minzoom': 13,
          'layout': this.textLayout,
          'paint': this.textPaint,
        })

        const stylingCondition = ['match', ['get', 'Kleur']]
        const defaultColors = [[''], 'lightgrey']

        Object.keys(this.colors).forEach(color => {
          stylingCondition.push(color, this.colors[color])
        })

        stylingCondition.push(...defaultColors)
        stylingCondition.push('lightgrey')

        const fillColorExpression = [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          '#f05a00', // selection color
          stylingCondition,
        ]
        this.$store.map.setPaintProperty(this.layerName, 'fill-color', fillColorExpression)

        this.registerLayerInteractions()

        EventBus.$emit('layers.national.loaded', {
          name: this.layerName,
        })

      })
    },
    removeLayerAndSource () {
      if (this.$store.map.getLayer(this.layerName)) {
        this.$store.map.removeLayer(this.layerName)
        this.$store.map.removeLayer(this.layerName + '-text')
        this.$store.map.removeSource(this.layerName)
      }
    },
      registerLayerInteractions() {
      const map = this.$store.map

      if (!map || !map.getLayer(this.layerName)) return

      this.unregisterLayerInteractions()

      map.on('mouseenter', this.layerName, this.onMouseEnter)
      map.on('mouseleave', this.layerName, this.onMouseLeave)
      map.on('click', this.layerName, this.onClick)
    },
    unregisterLayerInteractions() {
      const map = this.$store.map

      if (!map) return

      map.off('mouseenter', this.layerName, this.onMouseEnter)
      map.off('mouseleave', this.layerName, this.onMouseLeave)
      map.off('click', this.layerName, this.onClick)

      const canvas = map.getCanvas && map.getCanvas()
      if (canvas) {
        canvas.style.cursor = ''
      }
    },
    onMouseEnter() {
      const map = this.$store.map
      const canvas = map?.getCanvas && map.getCanvas()

      if (canvas) {
        canvas.style.cursor = 'pointer'
      }
    },
    onMouseLeave() {
      const canvas = this.$store.map?.getCanvas && this.$store.map.getCanvas()

      if (canvas) {
        canvas.style.cursor = ''
      }
    },
    onClick(e) {
      const map = this.$store.map
      const feature = e?.features && e.features[0]

      if (!map || !feature) return

      const featureState = this.buildFeatureStateParams(feature)

      if (!featureState) return

      if (this.selectedFeatureState &&
          this.selectedFeatureState.id === featureState.id) {
        return
      }

      this.clearSelectedState()

      this.selectedFeatureState = featureState
      map.setFeatureState(this.selectedFeatureState, { selected: true })
      EventBus.$emit('layers.national.whitespots.select', {
        name: this.layerName,
        feature,
        values: {
          statusPubliekePaal: feature.properties.Status ?? null,
          toelichting: Object.values(feature.properties)[0] ?? null,
        },
        lngLat: e.lngLat,
      })
    },
    clearSelectedState() {
      const map = this.$store.map

      if (!map || !this.selectedFeatureState) return

      map.setFeatureState(this.selectedFeatureState, { selected: false })
      this.selectedFeatureState = null
    },
    buildFeatureStateParams(feature) {
      if (!feature) return null

      const id = feature.id ?? feature.properties?.grid_ID

      if (id === undefined || id === null) return null

      return {
        id,
        source: this.sourceName,
        sourceLayer: this.source.layer,
      }
      
    }
  },
}
</script>
